import React, { useState, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { pathOr } from 'ramda';
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip as TooltipRecharts, Line, Legend } from 'recharts';
import { Legend as CustomLegend, Tick as CustomTick } from '../components';
import { Tooltip } from '../../tooltip';
import { Body1, Body2 } from '../../typography';
import { getColorShade } from '../../../../src/styled/utils';
const TooltipContent = ({ value, xTick }) => (React.createElement(React.Fragment, null,
    React.createElement(Body1, null, value),
    React.createElement(Body2, null, xTick)));
const CustomizedActiveDot = ({ cx, cy, stroke, fill, coordinate, payload, dataKeyXAxis, value }) => {
    const x = pathOr(0, ['x'], coordinate);
    const y = pathOr(0, ['y'], coordinate);
    const xTickValue = pathOr('', [dataKeyXAxis], payload);
    const isMouseNear = Math.sqrt(Math.pow(cx - x, 2) + Math.pow(cy - y, 2)) < 15;
    if (!isMouseNear)
        return null;
    return (React.createElement(Tooltip, { content: React.createElement(TooltipContent, { value: value, xTick: xTickValue }), isWhite: true },
        React.createElement("svg", { x: cx - 7.5, y: cy - 7.5, width: 30, height: 30, viewBox: "0 0 200 200" },
            React.createElement("circle", { cx: "50", cy: "50", r: "30", strokeWidth: "10", stroke: stroke, fill: fill }))));
};
const TOOLTIP_STYLES = { display: 'none' };
/**
 * Props for the SparklineChart component.
 *
 * @typedef {object} SparklineChartPropsT
 * @property {Array.<object>} [data] - Array of data points.
 * @property {Array.<string>} dataKeys - Array of keys to display in the chart.
 * @property {string} dataKeyXAxis - Key for the X axis data.
 * @property {LineType} [lineType='monotone'] - Type of the line in the chart.
 */
export const SparklineChart = ({ dataKeys, dataKeyXAxis, onMouseMove, onMouseLeave, lineType = 'monotone', ...props }) => {
    const [coordinate, setCoordinate] = useState(undefined);
    const theme = useTheme();
    const { chartColors, sizeUnit } = theme;
    const cartesianGridStroke = useMemo(() => getColorShade('surface', 'outline')({ theme }), [theme]);
    const handleMouseMove = (nextState, e) => {
        setCoordinate(nextState === null || nextState === void 0 ? void 0 : nextState.activeCoordinate);
        if (onMouseMove) {
            onMouseMove(nextState, e);
        }
    };
    const handleMouseLeave = (nextState, e) => {
        setCoordinate(undefined);
        if (onMouseLeave) {
            onMouseLeave(nextState, e);
        }
    };
    return (React.createElement(ResponsiveContainer, null,
        React.createElement(LineChart, { ...props, onMouseMove: handleMouseMove, onMouseLeave: handleMouseLeave },
            React.createElement(CartesianGrid, { vertical: false, strokeWidth: sizeUnit * 0.125, fill: cartesianGridStroke }),
            React.createElement(XAxis, { dataKey: dataKeyXAxis, axisLine: false, tickLine: false, tick: React.createElement(CustomTick, null) }),
            React.createElement(YAxis, { axisLine: false, tickLine: false, tickMargin: 20, tick: React.createElement(CustomTick, null) }),
            React.createElement(TooltipRecharts, { wrapperStyle: TOOLTIP_STYLES }),
            React.createElement(Legend, { content: React.createElement(CustomLegend, null) }),
            dataKeys.map((dataKey, index) => (React.createElement(Line, { key: dataKey, dataKey: dataKey, type: lineType, dot: false, activeDot: React.createElement(CustomizedActiveDot, { coordinate: coordinate, dataKeyXAxis: dataKeyXAxis }), stroke: chartColors[(index + 1) % chartColors.length], strokeWidth: 2 }))))));
};
